import classes from './App.module.css';
import { PATHS } from './constants/routes';
import { Navigate, useLocation, useNavigationType, useParams, useRoutes } from 'react-router-dom';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from './store/store';
import { fetchProducts } from './store/ProductSlice';
// import CategoryPage from './components/pages/showcasePages/CategoryPage/CategoryPage';
import ProductsPage from './components/pages/adminPages/ProductsPage/ProductsPage';
import UsersPage from './components/pages/adminPages/UsersPage/UsersPage';
import SettingsPage from './components/pages/adminPages/SettingsPage/SettingsPage';
import FeedbackPage from './components/pages/adminPages/FeedbackPage/FeedbackPage';
import CrptPage from './components/pages/adminPages/CrptPage/CrptPage';
import AdminLoginPage from './components/pages/adminPages/AdminLoginPage/AdminLoginPage';
import AdminProtectedRoute from './components/showcase/AdminProtectedRoute/AdminProtectedRoute';
import AdminPage from './components/pages/adminPages/AdminPage/AdminPage';
// import DiscountProductsPage from './components/pages/showcasePages/DiscountProductsPage/DiscountProductsPage';
import MainPage from './components/pages/showcasePages/MainPage/MainPage';
// import HomePage from './components/pages/showcasePages/HomePage/HomePage';
import CatalogPage from './components/CatalogPage/CatalogPage';
import HomePage from './components/HomePage/HomePage';
import AboutCompanyPage from './components/pages/showcasePages/AboutCompanyPage/AboutCompanyPage';
import ContactsPage from './components/ContactsPage/ContactsPage';
import HotelProductsPage from './components/pages/showcasePages/HotelProductsPage/HotelProductsPage';
import CustomersPage from './components/CustomersPage/CustomersPage';
import DeliveryPaymentPage from './components/pages/showcasePages/DeliveryPaymentPage/DeliveryPaymentPage';
import SleepTerritoryPage from './components/pages/showcasePages/SleepTerritoryPage/SleepTerritoryPage';
import BestSilkPage from './components/pages/showcasePages/BestSilkPage/BestSilkPage';
import LuxuryArtPage from './components/pages/showcasePages/LuxuryArtPage/LuxuryArtPage';
import AccountPage from './components/pages/showcasePages/AccountPage/AccountPage';
// import СonsentPage from './components/pages/showcasePages/СonsentPage/СonsentPage';
// import TermsPage from './components/pages/showcasePages/TermsPage/TermsPage';
import LoyaltyPage from './components/pages/showcasePages/LoyaltyPage/LoyaltyPage';
// import ReturnPage from './components/pages/showcasePages/ReturnPage/ReturnPage';
import ShowcasePage from './components/pages/showcasePages/ShowcasePage/ShowcasePage';
import { getFromLocalStorage } from './store/UserSlice';
// import WishlistPage from './components/pages/showcasePages/WishlistPage/WishlistPage';
import ProductSlugPade from './components/ProductSlugPage/ProductSlugPade';
import ProductPage from './components/pages/showcasePages/ProductPage/ProductPage';
import Loader from './components/UI/Loader/Loader';
// import CartPage from './components/pages/showcasePages/CartPage/CartPage';
import CartPage from './components/pages/showcasePages/CartPage/CartPageNew';
import OrdersPage from './components/pages/adminPages/OrdersPage/OrdersPage';
import CheckoutSuccessPage from './components/pages/showcasePages/CheckoutSuccessPage/CheckoutSuccessPage';
import FaqPage from './components/FaqPage/FaqPage';
import InDevelopment from './components/pages/showcasePages/InDevelopment/InDevelopment';
import NotFound from './components/pages/showcasePages/NotFound/NotFound';
import { Helmet } from 'react-helmet-async';
import { useYandexMetricaHits } from './components/metrics/useYandexMetricaHits';
import SiteMapPage from './components/pages/showcasePages/SiteMapPage/SiteMapPage';
import { fetchCategories } from './store/CategorySlice';
import { usePaidOrderCartReconcile } from './hooks/cartPage/usePaidOrderCartReconcile';
import CheckoutStatusPopup from './components/shared/CheckoutStatusPopup/CheckoutStatusPopup';
// import SitemapFooterLinkInjector from './components/shared/SitemapFooterLinkInjector/SitemapFooterLinkInjector';
// import { reachGoal } from './components/metrics/ym';

declare global {
    interface Window {
        prerenderReady?: boolean;
    }
}

const App = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { isLoading, products } = useSelector((state: RootState) => state.product);
    const isDataLoaded = !isLoading && products.length > 0;
    const location = useLocation();
    const navType = useNavigationType();

    useYandexMetricaHits();
    usePaidOrderCartReconcile();

    useEffect(() => {
        let raf = 0;
        const onScroll = () => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                try {
                    const key = 'scroll-positions';
                    const map: Record<string, number> = JSON.parse(sessionStorage.getItem(key) || '{}');
                    map[location.key] = window.scrollY;
                    sessionStorage.setItem(key, JSON.stringify(map));
                } catch {}
            });
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            cancelAnimationFrame(raf);
        };
    }, [location.key]);
    /** восстанавливаем/сбрасываем позицию при каждом переходе */
    useEffect(() => {
        if (location.hash) return;
        try {
            const map: Record<string, number> = JSON.parse(sessionStorage.getItem('scroll-positions') || '{}');
            const savedY = map[location.key] ?? 0;
            if (navType === 'POP') {
                window.scrollTo(0, savedY);
            } else {
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
            }
        } catch {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
    }, [location.key, location.hash, navType]);

    // SEO: универсальный компонент для установки Title/Description
    const PageSEO: React.FC<{ title: string; description?: string; canonical?: string }> = ({ title, description, canonical }) => (
        <Helmet>
            <title>{title}</title>
            {description ? <meta name="description" content={description} /> : null}
            {canonical ? <link rel="canonical" href={canonical} /> : null}
        </Helmet>
    );

    // SEO: извлекаем человекочитаемое имя категории по :url из данных товаров
    const CategorySEO: React.FC = () => {
        const { url } = useParams();
        const { products: all } = useSelector((s: RootState) => s.product);
        const { categories } = useSelector((s: RootState) => s.category);
        const findCategoryName = (slug?: string): string | undefined => {
            if (!slug) return undefined;
            const lower = slug.toLowerCase();
            for (const p of all as any[]) {
                const cat = (p?.category ?? p?.section ?? p?.categoryId ?? p?.sectionId) as any;
                if (!cat) continue;
                const candidates = [String(cat?.url ?? ''), String(cat?.slug ?? ''), String(cat?.code ?? '')].filter(Boolean);
                if (candidates.some((c) => c.toLowerCase() === lower)) {
                    const name = String(cat?.name ?? cat?.title ?? '');
                    if (name) return name;
                }
            }
            const decoded = decodeURIComponent(slug);
            const spaced = decoded.replace(/[-_]+/g, ' ').trim();
            if (!spaced) return undefined;
            return spaced.charAt(0).toUpperCase() + spaced.slice(1);
        };
        const name = findCategoryName(url) ?? 'Категория';
        const defaultTitle = `${name} – купить по выгодной цене с доставкой по России`;
        const defaultDescription = `Подберите ${name} из натурального шелка премиум-класса в S-Gabai. Прямые поставки из Китая, высокое качество, гипоаллергенные материалы. Доставка по всей России. Тел.: +7 495 280-60-55`;
        const origin = (process.env.REACT_APP_CANONICAL_ORIGIN || 'https://s-gabai.ru').replace(/\/$/, '');
        const slugSafe = String(url || '')
            .trim()
            .toLowerCase();
        const canonicalHref = new URL(`/catalog/${slugSafe}/`, origin + '/').toString();
        const matched = categories.find(
            (c) =>
                String(c?.url || '')
                    .trim()
                    .toLowerCase() === slugSafe,
        );
        const title = String(matched?.title ?? '').trim() || defaultTitle;
        const description = String(matched?.description ?? '').trim() || defaultDescription;
        return <PageSEO title={title} description={description} canonical={canonicalHref} />;
    };

    // SEO: метатеги для карточки товара по :id
    const ProductSEO: React.FC = () => {
        const { id } = useParams();
        const location = useLocation();
        const { products: all } = useSelector((s: RootState) => s.product);
        const product = (all as any[]).find((p) => String(p?.id ?? p?._id ?? '') === String(id));
        const productName: string = String(product?.name ?? product?.title ?? product?.displayName ?? '').trim() || 'Товар';
        const sectionName: string = String(product?.category?.name ?? product?.section?.name ?? '').trim() || 'категории';
        const title = `${productName} – купить ${sectionName} с доставкой по России`;
        const description = `Купите ${productName} в категории «${sectionName}» на сайте S-Gabai. Шелк Mulberry или Tussah, премиальное качество, доставка по всей России. Оформите заказ онлайн или по тел.: +7 495 280-60-55`;
        const origin = (process.env.REACT_APP_CANONICAL_ORIGIN || 'https://s-gabai.ru').replace(/\/$/, '');
        const slug = String(product?.slug ?? '').trim();
        const canonicalPath = slug ? `/${slug}/` : `${location.pathname.replace(/\/?$/, '/')}`;
        const canonicalHref = new URL(canonicalPath, origin + '/').toString();
        return (
            <Helmet>
                <title>{title}</title>
                <meta name="description" content={description} />
                <link rel="canonical" href={canonicalHref} />
            </Helmet>
        );
    };

    // Глобальный каноникал
    const CanonicalSelf: React.FC = () => {
        const location = useLocation();
        const origin = (process.env.REACT_APP_CANONICAL_ORIGIN || (typeof window !== 'undefined' ? window.location.origin : 'https://s-gabai.ru')).replace(
            /\/$/,
            '',
        );
        const path = location.pathname.replace(/\/?$/, '/'); // без query/hash и всегда со слэшем
        const canonicalHref = new URL(path, origin + '/').toString();
        return (
            <Helmet>
                <link rel="canonical" href={canonicalHref} />
            </Helmet>
        );
    };

    const routes = useRoutes([
        {
            path: PATHS.main,
            element: <MainPage />,
            children: [
                {
                    index: true,
                    element: (
                        <>
                            <Helmet>
                                <link rel="canonical" href="https://s-gabai.ru/" />
                            </Helmet>
                            <HomePage />
                        </>
                    ), // контент для главной страницы
                },
                // Дочерние маршруты с абсолютными путями
                {
                    path: '/sleep-territory',
                    element: <SleepTerritoryPage />,
                },
                {
                    path: '/best-silk',
                    element: <BestSilkPage />,
                },
                {
                    path: '/luxury-art',
                    element: <LuxuryArtPage />,
                },
                {
                    path: '/about-company',
                    element: (
                        <>
                            <PageSEO
                                title="О компании S-Gabai – поставщик шелковых подушек и одеял из Китая"
                                description="Узнайте о бренде S-Gabai – прямые поставки шелковых изделий из Китая, премиум-класса, сертифицированное качество. Работаем по всей России. Тел.: +7 495 280-60-55"
                                canonical="https://s-gabai.ru/about-company/"
                            />
                            <AboutCompanyPage />
                        </>
                    ),
                },
                {
                    path: '/contacts',
                    element: <ContactsPage />,
                },
                {
                    path: '/hotel-products',
                    element: (
                        <>
                            <PageSEO
                                title="Продукция для гостиниц – поставщик шелковых подушек и одеял S-Gabai"
                                description="Узнайте о бренде S-Gabai – прямые поставки шелковых изделий из Китая, премиум-класса, сертифицированное качество. Работаем по всей России. Тел.: +7 495 280-60-55"
                            />
                            <HotelProductsPage />
                        </>
                    ),
                },
                {
                    path: '/customers',
                    element: (
                        <>
                            <PageSEO
                                title="Покупателям S-Gabai — оферта, возврат, конфиденциальность"
                                description="Вся важная информация для покупателей S-Gabai: оферта, политика персональных данных, возврат товара и форма возврата. Условия покупки и поддержка."
                            />
                            <CustomersPage />
                        </>
                    ),
                },
                {
                    path: '/delivery-payment',
                    element: (
                        <>
                            <PageSEO
                                title="Доставка и оплата – интернет-магазин шелковых изделий S-Gabai"
                                description="Условия доставки и оплаты в интернет-магазине S-Gabai. Отправка по всей России. Безопасная оплата и быстрая логистика. Тел.: +7 495 280-60-55"
                            />
                            <DeliveryPaymentPage />
                        </>
                    ),
                },
                // {
                //     path: '/wishlist',
                //     element: isDataLoaded ? (
                //         <>
                //             <PageSEO
                //                 title="Избранное – интернет-магазин шелковых изделий S-Gabai"
                //                 description="Избранные товары в интернет-магазине S-Gabai. Отправка по всей России. Безопасная оплата и быстрая логистика. Тел.: +7 495 280-60-55"
                //             />
                //             <WishlistPage />
                //         </>
                //     ) : (
                //         <Loader />
                //     ),
                // },
                {
                    path: PATHS.wishlistLegacy,
                    element: <Navigate to={PATHS.wishlist} replace />,
                },
                {
                    path: '/cart',
                    element: isDataLoaded ? (
                        <>
                            <PageSEO
                                title="Корзина – интернет-магазин шелковых изделий S-Gabai"
                                description="Корзина товаров в интернет-магазине S-Gabai. Отправка по всей России. Безопасная оплата и быстрая логистика. Тел.: +7 495 280-60-55"
                            />
                            <CartPage />
                        </>
                    ) : (
                        <Loader />
                    ),
                },
                {
                    path: 'cart/success',
                    element: <CheckoutSuccessPage />,
                },
                {
                    path: PATHS.account,
                    element: (
                        <>
                            <PageSEO
                                title="Личный кабинет – интернет-магазин шелковых изделий S-Gabai"
                                description="Личный кабинет в интернет-магазине S-Gabai. Отправка по всей России. Безопасная оплата и быстрая логистика. Тел.: +7 495 280-60-55"
                            />
                            <AccountPage />
                        </>
                    ),
                },
                {
                    path: PATHS.accountSection,
                    element: (
                        <>
                            <PageSEO
                                title="Личный кабинет – интернет-магазин шелковых изделий S-Gabai"
                                description="Личный кабинет в интернет-магазине S-Gabai. Отправка по всей России. Безопасная оплата и быстрая логистика. Тел.: +7 495 280-60-55"
                            />
                            <AccountPage />
                        </>
                    ),
                },
                // {
                //     path: '/consent',
                //     element: <СonsentPage />,
                // },
                // {
                //     path: '/terms',
                //     element: <TermsPage />,
                // },
                {
                    path: '/loyalty',
                    element: <LoyaltyPage />,
                },
                // {
                //     path: '/return',
                //     element: <ReturnPage />,
                // },
                {
                    path: '/site-map',
                    element: <SiteMapPage />,
                },
            ],
        },
        {
            path: '/:slug',
            element: isDataLoaded ? (
                <>
                    {/* <SlugCanonicalSEO /> */}
                    <ProductSlugPade />
                </>
            ) : (
                <Loader />
            ),
        },
        {
            path: PATHS.showcase, // /catalog
            element: (
                <>
                    <ShowcasePage />
                </>
            ),
            children: [
                {
                    index: true, // по умолчанию /catalog
                    element: isDataLoaded ? (
                        <>
                            <PageSEO
                                title="Премиальные подушки и одеяла из шелка - интернет-магазин S-Gabai"
                                description="Натуральные шелковые подушки и одеяла Mulberry и Tussah из Китая. Премиум качество, гипоаллергенность, прямые поставки. Доставка по всей России."
                                canonical="https://s-gabai.ru/catalog/"
                            />
                            <CatalogPage />
                        </>
                    ) : (
                        <Loader />
                    ),
                },
                {
                    path: ':url', // /catalog/podushki и т.д.
                    children: [
                        {
                            index: true,
                            element: isDataLoaded ? (
                                <>
                                    <CategorySEO />
                                    <CatalogPage />
                                </>
                            ) : (
                                <Loader />
                            ),
                        },
                        {
                            path: ':id',
                            element: isDataLoaded ? (
                                <>
                                    <ProductSEO />
                                    <ProductPage />
                                </>
                            ) : (
                                <Loader />
                            ),
                        },
                    ],
                },
                {
                    path: 'bedlinens',
                    element: <InDevelopment />,
                },
            ],
        },
        {
            path: '/faq',
            element: <FaqPage />,
        },
        {
            path: '/admin/login',
            element: <AdminLoginPage />,
        },
        {
            path: PATHS.admin,
            element: (
                <AdminProtectedRoute>
                    <AdminPage />
                </AdminProtectedRoute>
            ),
            children: [
                {
                    index: true,
                    path: PATHS.orders,
                    element: <OrdersPage />,
                },
                {
                    path: PATHS.products,
                    element: <ProductsPage />,
                },
                {
                    path: PATHS.users,
                    element: <UsersPage />,
                },
                {
                    path: PATHS.settings,
                    element: <SettingsPage />,
                },
                {
                    path: PATHS.crpt,
                    element: <CrptPage />,
                },
                {
                    path: PATHS.feedback,
                    element: <FeedbackPage />,
                },
            ],
        },
        {
            path: '*',
            element: <NotFound />,
        },
    ]);

    useEffect(() => {
        const boot = async () => {
            try {
                const isProd = process.env.NODE_ENV === 'production';
                if (isProd) {
                    const base = process.env.REACT_APP_SERVER_URL || 'http://localhost:5000';
                    const r = await fetch(`${base}/version`, { credentials: 'include' });
                    if (r.ok) {
                        const { version } = await r.json();
                        const serverVersion = String(version || '').trim();
                        const build = String(process.env.REACT_APP_BUILD_ID || '').trim();
                        if (serverVersion && serverVersion !== build) {
                            const already = sessionStorage.getItem('reloadedForVersion');
                            if (already !== serverVersion) {
                                sessionStorage.setItem('reloadedForVersion', serverVersion);
                                const url = new URL(window.location.href);
                                url.searchParams.set('v', serverVersion);
                                window.location.replace(url.toString());
                                return;
                            }
                        }
                    }
                }
            } catch {}
            dispatch(getFromLocalStorage('wishlist'));
            dispatch(getFromLocalStorage('cart'));
            dispatch(fetchProducts());
            dispatch(fetchCategories());
        };
        void boot();
    }, [dispatch]);

    useEffect(() => {
        // SEO: если Helmet уже установил свою мету (data-rh="true"), удаляем статические дубликаты из index.html на любом роуте
        try {
            const hasHelmetDesc = document.head.querySelector('meta[name="description"][data-rh="true"]');
            if (hasHelmetDesc) {
                document.head.querySelectorAll('meta[name="description"]:not([data-rh])').forEach((n) => n.parentNode?.removeChild(n));
            }
            const hasHelmetCanonical = document.head.querySelector('link[rel="canonical"][data-rh="true"]');
            if (hasHelmetCanonical) {
                document.head.querySelectorAll('link[rel="canonical"]:not([data-rh])').forEach((n) => n.parentNode?.removeChild(n));
            }
        } catch {}
    }, [location.pathname]);

    // ---- PRERENDER: помечаем этапы готовности снимка ----
    useEffect(() => {
        try {
            window.prerenderReady = false;
        } catch {}
    }, [location.pathname]);
    useEffect(() => {
        if (isDataLoaded) {
            const t = setTimeout(() => {
                try {
                    window.prerenderReady = true;
                } catch {}
            }, 0);
            return () => clearTimeout(t);
        }
    }, [isDataLoaded, location.pathname]);

    return (
        <div className={classes.app}>
            <CanonicalSelf />
            {routes}
            <CheckoutStatusPopup />
        </div>
    );
};

export default App;
